// Mediator class should connect every feature of Mirror into itself
// It should handle the following deps

// videorecordings
// WebrtcManager Instance
// CommandManager Instance
// StreamManager instance
// UI Instances

// Imports
import StreamManagerSingleton from '../webrtc/StreamManager';
import Stream from '../webrtc/Stream';

import { videoRecorderSingleton } from './videorecordings';
import { modifyCodecPayloadType, defaultBandwithOptions } from '../webrtc/sdputils';

import { setupUI as setupMirrorUI } from './ui';
import commandsManager from '../general/CommandsManager';
import messageBroker from '../general/messageBroker';
import { setupSoundMeter } from '../webaudio/soundmeter';
import VideoPlayerControls from './videoplayer';
import { addTask } from '../utils/status-indicator';
import {
  handleEndOfSession, setupUI, toggleCameraAudio, updateLocalAudioUI,
} from '../../javascript/common/ui';
import {
  changeVideoResolution,
  resetVideoSource,
  changeAudioDestination,
  setupMediaDevices,
  gotDevices,
} from '../webrtc/mediaDevices';

import { localNotify } from '../utils/notifications';
import { detectBrowser } from '../utils/browser';
import { CommonManager, getPeerConnection } from '../webrtc/common-manager';

const qualitySettings = {
  video: {
    quality: '1080p', // default Video Quality "p", used for "magic numbers"
    lowDataMode: 1250, // bitrate for low data mode around 300kbps
    lowDataModeRatio: 1.5, // this gets 720 out of a 1080p stream( 1080 / 720 = 1.5)

    highDataMode: 5000, // birate for high data mode around 1.2mbps (?) :checkiftrue
    useModifiedPayloadCodec: true,
  },
  audio: {
    bitrate: 32,
    hasDTX: true,
    hasStereo: true,
  },
};

class MirrorManager extends CommonManager {
  constructor(role, name, publicKey, roomKey) {
    console.time('Booting up Mirror Manager');
    super('mirror', role, name, publicKey, roomKey);

    // update qualitySettings if role
    if (role === 'host') {
      qualitySettings.audio.bitrate = 320;
      qualitySettings.audio.hasDTX = true;
    }

    this.sessionEnded = false;

    this.privateRoomOn = false;
    this.directorToSetOn = false;
    this.lowDataModeOn = false;
    this.showParticipantInfo = false;

    // Streams
    this.audioStream = null;
    this.cameraAudioStream = null;

    this.localAudio = null;
    this.cameraVideo = null;
    this.cameraAudio = null;

    console.log('Booting up Mirror Manager');
    this.initialSetup();
    console.timeEnd('Booting up Mirror Manager');
  }

  NewPeer(p) {
    console.log('New peer is', p);
    getParticipantList().addNewPeer(p);
    try {
      getMirrorMobileDrawerParticipantList().addNewPeer(p);
    } catch (e) {
      console.warn('No mobile drawer?', e);
    }
    if (this.role == 'host') {
      this.sendRoomStatus(p.public_key);
    }
    this.peerList.push(p);
  }

  sendRoomStatus(public_key) {
    if (this.role != 'host') return;
    // Send room status to the connected peer
    console.log('Sending to peer with', public_key);
    // Send state of Director to set if product is mirror
    messageBroker.sendCommand('director_to_set', public_key, { directorToSetOn: this.directorToSetOn });
    messageBroker.sendCommand('active_until', public_key, { activeUntil: parseInt(window.activeUntil * 1000) - new Date() });
    messageBroker.sendCommand('set_low_data_mode', public_key, { lowDataModeOn: manager.lowDataModeOn });
	  messageBroker.sendCommand('mirror--show-participant-info', public_key, { show: this.showParticipantInfo });

    if (vm.$refs.orchestrator && vm.$refs.orchestrator.$refs) {
      const orchestrator = vm.$refs.orchestrator.$refs;
      // Send rotation angle
      messageBroker.sendCommand('rotate_viewport', public_key, { angle: orchestrator.viewport.angle });
      // Send aspect ratio
      messageBroker.sendCommand('mask_viewport', public_key, { aspectRatio: orchestrator.viewport.aspectRatio });

      if (typeof orchestrator.videoTakeList !== 'undefined') orchestrator.videoTakeList.sendVideoTakes();

      // send continuity
      if (typeof orchestrator.continuity !== 'undefined') orchestrator.continuity.sendContinuity();
      // send shooting board
      if (typeof orchestrator.shootingBoard !== 'undefined') orchestrator.shootingBoard.sendShootingBoard(public_key);
      // send shooting plan
      if (typeof orchestrator.shootingPlan !== 'undefined') orchestrator.shootingPlan.sendShootingPlan(public_key);
      // send stills
      if (typeof orchestrator.stills !== 'undefined') orchestrator.stills.sendStills(public_key);
    }
  }

  beforeUnloadHook(event) {
    if (this.sessionEnded == true) return;
    // Cancel the event
    event.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
    // Chrome requires returnValue to be set
    event.returnValue = '';
  }

  // initializes components and establishes a comunication path between them
  // Should setup
  // get the default audio streams
  // set up all the UI
  // set up the websocket connection and login to the room
  initialSetup() {
    // This is needed for:
    // - to check whether to listen to input changes or not in media devices
    // - It may be used for something else
    window.isHost = this.role == 'host';

    CommonManager.prototype.initialSetup.call(this);

    // Specific
    this.setupPrivateRoom();
    this.setupPlugins();

    $('#toggleDirectorToSet input').removeAttr('disabled');
    $('#togglePrivateRoom input').removeAttr('disabled');

    this.auxCameraEnabled = false;
    commandsManager.registerNewCommand('mirror--toggle_aux_camera', (data) => {
      // if (this.auxCameraEnabled == data.enabled) {
      //   console.log("already in the proper state")
      //   return
      // }
      this.auxCameraEnabled = data.enabled;
      // disable the event temporarily
      window.cancelShowViewportPlaceholder = true;

      const mainVideo = document.querySelector('#remoteVideo');
      const aux = mainVideo.srcObject;
      const auxVideo = document.querySelector('#auxVideo');
      mainVideo.srcObject = auxVideo.srcObject;
      auxVideo.srcObject = aux;
    });
    commandsManager.registerNewCommand('toggle_ptt', (data) => {
      this.toggleLocalAudio(data.args.enabled);
    });

    commandsManager.registerNewCommand('director_to_set', (data) => {
      if (data.args.directorToSetOn == this.directorToSetOn) return;
      const directorToSetToggle = $('#toggleDirectorToSet');
      directorToSetToggle.children('input').prop('checked', data.args.directorToSetOn);
      localNotify(`Talk to the set is now ${data.args.directorToSetOn ? 'on' : 'off'}`);
      $('#on-set-talk-badge').toggleClass('hidden', !data.args.directorToSetOn);

      this.directorToSetOn = data.args.directorToSetOn;
      $("[id^='director_to_set_']").each(function () {
        $(this)[0].muted = !data.args.directorToSetOn;
      });
    });

    messageBroker.registerNewMessageType('camera-audio', (data) => {
      // show soundmeter moving
      toggleCameraAudio(data);
    });

    commandsManager.registerNewCommand('mirror--show-participant-info', (data) => {
      if (data.args.show) {
        document.querySelector('#participantInfo').classList.remove('hidden');
      } else {
        document.querySelector('#participantInfo').classList.add('hidden');
      }
    });
  }

  endSession(reason) {
    console.trace('end the session due to', reason);
    if (this.role == 'host') {
      const confirmString = 'Are you sure you want to end the session? This will disconnect everyone.';
      const r = confirm(confirmString);
      if (r === true) {
        this.sessionEnded = true;
        commandsManager.sendAllCommand('end_session');
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } else {
      }
      return;
    }
    this.sessionEnded = true;
    window.clearInterval(window.timer);
    window.timer = null;

    if (this.sock) this.sock.stop();
    StreamManagerSingleton.removeAllStreams();
    this.handleEndOfSession();
    if (this.rtc && this.rtc.pc) {
      this.rtc.pc.Close();
      this.rtc.pc = null;
    }
    if (reason == 'USER') {
      document.querySelector('#hangup-button').classList.add('hidden');
      document.querySelector('#hangup-button').classList.add('hidden');
      document.querySelector('#endOfSession .host-disconnect-message').classList.add('hidden');
      document.querySelector('#endOfSession .user-disconnect-message').classList.remove('hidden');
    }
  }

  setupMediaDevicesManager() {
    StreamManagerSingleton.on('FINISHED_GETTING_STREAMS', () => {
      console.log('Finished getting tracks, this means we must set the new tracks on the PCs');
      const newTracks = StreamManagerSingleton.getTracks('director', this.role);

      // set the tracks in the mirror-webrtc-manager
      this.rtc.SetRTCTracks(newTracks);
      // this should be called in the future or as a callback as it may take a way to set the tracks
      // for now lets do it in 2 seconds in the future
      window.setTimeout(() => {
        if (this.lowDataModeOn) {
          this.enableLowDataMode();
        } else {
          this.disableLowDataMode();
        }
      }, 2000);
    });

    const browser = detectBrowser(window);
    this.browser = browser;

    // Media devices
    const subscriber = setupMediaDevices(browser, this.product, this.role);
    subscriber.on('NOTIFY', (message, type) => {
      localNotify(message, type);
    });

    // this should be call after subscriber, it is very important
    if (this.role == 'host') {
      // Set the listeners on the appropiate places
      navigator.mediaDevices
        .enumerateDevices()
        .then(gotDevices)
        .catch(handleError);
    } else {
      StreamManagerSingleton.refresh();
    }




    // this event is fired whenever the value of a selector changes,
    // when it does we should set up the new constraints on the manager
    // and tell the RTC to refresh the streams (and call the users)
    subscriber.on('GET_USER_MEDIA', (constraints) => {
      Object.keys(constraints).forEach((key) => {
        StreamManagerSingleton.setConstraints(key, constraints[key]);
      });
      // TODO: tell the RTC to refresh the user media why?
      console.log('Get user media due to event');
      StreamManagerSingleton.refresh();
    });
    // subscriber.on('SEND_SESSION_INFO', () => {
    //   sendSessionInfo();
    // });
  }

  handlePeerDisconnected(data) {
    // do the usual
    CommonManager.prototype.handlePeerDisconnected.call(this, data);
    let peerRole = '';
    this.peerList.forEach((p) => {
      if (p.public_key == data.public_key) {
        peerRole = p.role;
      }
    });
    if (peerRole == '') { console.error('Couldnt find role for peer'); }
    if (peerRole == 'host') {
      // if its the host dim the video for now
      const videoElem = document.querySelector('#remoteVideo');
      if (videoElem) {
        videoElem.classList.add('opacity-blink');
      }
    }
  }

  handleTrack({ streams, track }) {
    const [peerRole, peerKey, kind, label] = streams[0].id.split('__');
    if (track.kind == 'video') {
      this.handleVideoStream(streams[0], label);
    }
    if (track.kind == 'audio') {
      this.handleAudioStream(streams[0], peerRole, peerKey, label);
      changeAudioDestination();
    }
  }

  sendMessage(message, type, to) {
    messageBroker.sendMessage(message, type, to);
  }

  setupPlugins() {
    videoRecorderSingleton.manager = this;
    videoRecorderSingleton.getCommands().forEach((command) => {
      commandsManager.registerCommand(command);
    });

    const browser = detectBrowser(window);
    this.browser = browser;
    this.videoPlayerPlugin = new VideoPlayerControls(this, browser);
    this.videoPlayerPlugin.getCommands().forEach((command) => {
      commandsManager.registerCommand(command);
    });
  }

  disableLowDataMode() {
    console.log('disabling low data mode');
    this.lowDataModeOn = false;
    videoRecorderSingleton.setLowDataMode(false);
    this.rtc.SetBitrateForVideo(qualitySettings.video.highDataMode, 1);
  }

  enableLowDataMode() {
    console.log('Enabling low data mode');
    this.lowDataModeOn = true;
    videoRecorderSingleton.setLowDataMode(true);
    // lowDataModeRatio: 1.5, // this gets 720 out of a 1080p stream ( 1080 / 720 = 1.5)
    this.rtc.SetBitrateForVideo(qualitySettings.video.lowDataMode, qualitySettings.video.lowDataModeRatio);
  }

  setupPrivateRoom() {
    commandsManager.registerNewCommand('private-room--toggled', (data) => {
      // update UI
      const aecToggle = $('#togglePrivateRoom');
      aecToggle.children('input').prop('checked', data.args);
      aecToggle.children('input').prop('disabled', false);
      this.onPrivateRoomActivation(data.args);
      // window.privateModeOn = data.args.privateModeOn;
    });
    commandsManager.registerNewCommand('private-room--waiting', (data) => {
      const aecToggle = $('#togglePrivateRoom');
      aecToggle.children('input').attr('disabled', true);
    });
  }

  handleVideoStream(stream, label) {
    const videoElem = $('#remoteVideo')[0];

    if (videoElem == null) {
      console.error('Error when trying to handle video stream, no canvas found');
      return;
    }
    console.log('VIDEO ', label);
    if (label == 'DEMO_CAMERA') {
      let video = document.querySelector('#auxVideo');
      if (this.auxCameraEnabled) {
        video = document.querySelector('#remoteVideo');
      }
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;
      video.controls = false;
      video.play();
      video.onresize = () => {
        log(`Video stream is ${video.videoWidth}x${video.videoHeight}`);
      };
      return;
    }

    const activeTrack = null;
    // stream.getVideoTracks().forEach((track) => {
    //   if (track.enabled === true) { activeTrack = track; }
    // });
    // const newStream = new MediaStream([activeTrack]);
    videoElem.srcObject = stream;
    videoElem.classList.remove('opacity-blink');
    console.log('Remote video stream added.', stream.id);
    $('#viewportPlaceholderText').addClass('hidden');
  }

  handleAudioStream(stream, peerRole, id, label) {
    const peer = getPeerConnection(id);
    if (this.role !== 'host' && peerRole === 'host') {
      this.handleAudioFromHost(stream, peerRole, id, label);
    } else {
      this.handleAudioFromPeer(stream, peerRole, id);
      // setup soundmeter
      peer.component.addStream(stream);
    }
    console.log('Track added status = connected');
    peer.status = 'connected';
  }

  addTask(task) {
    return addTask(task);
  }

  handleAudioFromPeer(stream, peerRole, target) {
    console.log('handling audio from peer', stream, peerRole, target, this.role);
    // HACK to make audio work to  "split" diferent sources https://bugs.chromium.org/p/webrtc/issues/detail?id=5525
    // if this tag is not set to a dummy thing the createMediaStreamSource wont work
    const auxAudio = $(`#aux_audio_${target}`)[0];
    if (auxAudio) {
      auxAudio.srcObject = stream;
      auxAudio.muted = true; // aux audio is always muted
    }
    // # END OF HACK
    let audio = $(`#audio_${target}`)[0];
    // this.audioStream = stream;
    if (audio == null) {
      console.error('couldnt find the audio element');
      audio = document.createElement('audio');
      document.querySelector('body').appendChild(audio);
      audio.srcObject = stream;
      audio.autoplay = true;
      audio.muted = false;
      audio.controls = true;
      audio.playsInline = true;
      audio.dataset.role = peerRole;
      audio.play();
      return;
    }

    if (this.role === 'host') {
      const ctx = new AudioContext();
      const srcNode = ctx.createMediaStreamSource(stream);
      const destNode = ctx.createMediaStreamDestination();
      srcNode.connect(destNode);
      if (peerRole === 'director' || peerRole === 'production') { // @Mirror
        const assistantAudio = $(`#director_to_set_${target}`)[0];
        assistantAudio.srcObject = destNode.stream;
        assistantAudio.muted = !this.directorToSetOn;
        audio.srcObject = stream;
      } else if (peerRole === 'guest') { // @Mirror and @Anywhere
        audio.srcObject = stream;
      } else {
        audio.srcObject = stream;
      }
    } else {
      audio.srcObject = stream;
    }
  }

  handleAudioFromHost(stream, peerRole, target, label) {
    const peer = getPeerConnection(target);
    if (label === 'LOCAL_AUDIO') {
      this.audioStream = stream;
      const audio = $(`#audio_${target}`)[0];
      audio.srcObject = this.audioStream;
      // setupSoundMeter('#soundMeterHost', this.audioStream, target, 'bar'); (for anywhere)
      peer.component.addStream(stream);
    } else if (label === 'CAMERA_AUDIO') {
      console.log('setting up camera audio', stream.id);
      this.cameraAudioStream = stream;
      // HACK to make audio work to  "split" diferent sources https://bugs.chromium.org/p/webrtc/issues/detail?id=5525
      const cameraAudio = $(`#camera_audio_${target}`)[0];
      cameraAudio.srcObject = this.cameraAudioStream;
      peer.component.addSecondaryStream(stream);
    } else {
      console.log('UKNOWN TRACK, adding to DOM anyways');
      const newAudio = document.createElement('audio');
      newAudio.setAttribute('data-id', target);
      newAudio.setAttribute('data-role', peerRole);
      newAudio.setAttribute('autoplay', 'autoplay');
      newAudio.setAttribute('playsinline', 'playsinline');
      newAudio.srcObject = stream;
      // newAudio.setAttribute('controls', 'controls');
      document.body.appendChild(newAudio);
    }
  }

  handleVideoStreamRemoved(peerRole, id, label) {
    // Show it in the UI
    const videoElem = document.querySelector('#remoteVideo');
    if (videoElem) {
      videoElem.classList.remove('opacity-blink');
      videoElem.srcObject = null;
      $('#viewportPlaceholderText').removeClass('hidden');
    }
  }

  // RTC Methods

  // features used in mirror/UI.js
  togglePrivateRoom(toggled) {
    this.privateRoomOn = toggled;
    $('#private-mode-badge').toggleClass('hidden', !toggled);
    if (this.privateRoomOn) {
      messageBroker.send({
        type: 'command--room',
        command: 'private-room--enable',
      });
    } else {
      messageBroker.send({
        type: 'command--room',
        command: 'private-room--disable',
      });
    }
  }
  // } else if (data.message === 'PRIVATE_ROOM_TOGGLE_SUCCESSFULLY') {

  toggleDirectorToSet(toggled) {
    this.directorToSetOn = toggled;
    const directorToSetCommand = commandsManager.getCommandById('director_to_set');
    directorToSetCommand.sendToRole('production', { directorToSetOn: toggled });
    directorToSetCommand.sendToRole('host', { directorToSetOn: toggled });
    $('#on-set-talk-badge').toggleClass('hidden', !toggled);
  }

  setupUI() {
    console.log('Setting up mirror ui');
    const setPTT = false;
    setupUI(this, setPTT);
    setupMirrorUI(commandsManager, this);
  }

  addLocalAudio(streamManager) {
    const trackEnabled = true;
    const localAudioStream = new Stream({
		  label: 'LOCAL_AUDIO',
		  kind: 'AUDIO',
		  constraints: { audio: true, video: false },
		  enabled: trackEnabled,
    });

    updateLocalAudioUI(trackEnabled);
    localAudioStream.on('update[enabled]', updateLocalAudioUI);

    localAudioStream.on('GOT_STREAM', (label, stream) => {
		  // if (webAudio) stream = webAudio.applyFilter(stream);
		  // reset Soundmeters
		  setupSoundMeter('.soundMeterMain', stream);
		  $('#localAudio')[0].srcObject = stream;

      this.toggleLocalAudio(false);

		  // @Mirror
		  const meComponent = getMeParticipantComponent();
      if (meComponent && meComponent.addStream) {
        meComponent.addStream(stream);
      }
      // get soundmeter to add stream
      if (vm.$refs.ptt) {
        vm.$refs.ptt.addStream(stream);
      }
      if (vm.$refs.pttMobile) {
        vm.$refs.pttMobile.addStream(stream);
      }

      // get soundmeter to add stream
      if (vm.$refs.orchestrator) {
        if (vm.$refs.orchestrator.$refs.ptt) {
          vm.$refs.orchestrator.$refs.ptt.addStream(stream);
        }
        if (vm.$refs.orchestrator.$refs.pttMobile) {
          vm.$refs.orchestrator.$refs.pttMobile.addStream(stream);
        }
      }

		  // mute everyone's mic unless they are a talent or a mirror host that is already setup
    }); // end of got stream event;

    this.localAudio = localAudioStream;

    streamManager.addStream(localAudioStream);
    return streamManager;
  }

  addCameraAudio(streamManager) {
    const cameraAudioStream = new Stream({
		  label: 'CAMERA_AUDIO', kind: 'AUDIO', constraints: { audio: true, video: false }, enabled: false,
    });
    cameraAudioStream.on('update[enabled]', (enabled) => {
		  const muteButton = $(".muteMicrophone[data-id='camera']");
		  muteButton.children('input').prop('checked', enabled);
		  $('#cameraAudioOff').toggleClass('hidden', enabled);
    });
    cameraAudioStream.on('GOT_STREAM', (label, stream) => {
		  videoRecorderSingleton.setAudioStream(stream);
		  setupSoundMeter('#soundMeterCamera', stream);
		  if ($('#localAudio2').length > 0) $('#localAudio2')[0].srcObject = stream;
    }); // end of the got stream event

    this.cameraAudio = cameraAudioStream;

    streamManager.addStream(cameraAudioStream);
    return streamManager;
  }

  addCameraVideo(streamManager) {
    const cameraVideoStream = new Stream({
      label: 'CAMERA_VIDEO',
      kind: 'VIDEO',
      constraints: {
        audio: false,
        video: true,
      },
    });
    cameraVideoStream.on('update[enabled]', (enabled) => {
		  $('#viewportPlaceholderText').toggleClass('hidden', enabled);
		  const muteButton = $("#muteVideoFeed[data-id='camera']");
		  muteButton.children('input').prop('checked', enabled);
    });
    cameraVideoStream.on('GOT_STREAM', (label, stream) => {
      videoRecorderSingleton.setVideoStream(stream);
      // after setting the video recorder lets test if the stream is actually "true 1080p"

      const warning = "We've detected that your video feed is set to a resolution higher than our recommended 1080p. Real-time streaming won't be affected, but opting for higher resolutions like 4K may result in larger file sizes for your recordings and slower upload times. Please proceed as you wish, but keep in mind the potential trade-offs involving file size and upload speed.";
      videoRecorderSingleton.testVideoResolution()
        .then(() => {})
        .catch((error) => {
          // video is too large
          console.error('Selected video source is too large');
          alert(warning);
          // reset video source
          // resetVideoSource();
        });
      $('#viewportPlaceholderText').toggleClass('hidden', stream.getVideoTracks().length > 0);
      const changingVideoEl = document.querySelector('#localVideoSettingChangingCamera');
      if (changingVideoEl) {
        changingVideoEl.style.display = 'none';
      }

		  // Render Video Stream
		  this.renderVideoEl($('#localVideo')[0], stream);
		  this.renderVideoEl($('#localVideoSetting')[0], stream);
		  if ($('#localAudio2').length > 0) $('#localAudio2')[0].srcObject = stream;
    }); // end of the got stream event
    cameraVideoStream.on('updateVideoResolution', (value) => {
      changeVideoResolution(value);
    });
    cameraVideoStream.on('resetVideoSource', () => {
      resetVideoSource();
    });
    cameraVideoStream.on('notify', (error, level) => {
      localNotify(error, 'error');
    });
    this.cameraVideo = cameraVideoStream;

    streamManager.addStream(cameraVideoStream);
    return streamManager;
  }

  toggleLocalAudio(enabled) {
    this.localAudio.update({ enabled });
    // messageBroker.sendMessage({ enabled }, 'ptt');
  }

  toggleCameraAudio(enabled) {
    this.cameraAudio.update({ enabled });
    messageBroker.sendMessage({ enabled }, 'camera-audio');
  }

  toggleCameraVideo(enabled) {
    this.cameraVideo.update({ enabled });
  }

  setupStreams() {
    this.addLocalAudio(StreamManagerSingleton);
    if (this.role === 'host') {
      this.addCameraAudio(StreamManagerSingleton);
      this.addCameraVideo(StreamManagerSingleton);
    }
  }

  handleEndOfSession() {
    handleEndOfSession();
  }

  renderVideoEl(videoEl, stream) {
    if (videoEl == null) return;
    if (videoEl.srcObject == stream) return;
    [videoEl.muted, videoEl.volume] = [true, 0];
    if (stream.id == '') { videoEl.srcObject = null; return; } // (remove video)
    videoEl.srcObject = stream;
  }

  // Handles UI changes for host & director
  onPrivateRoomActivation(enabled) {
    // if (browser.browser === 'safari' && browser.version >= 605 && browser.iphone) {
    if (this.browser.browser === 'safari' && enabled == false && window.privateModeOn == true) {
      if (this.sessionEnded) return;
      this.sock.sock.close();
    }
    // update the status of the private room
    window.privateModeOn = enabled;

    if (this.role !== 'host' && this.role !== 'production') return;
    // Update the UI for production and host
    const aecToggle = $('#togglePrivateRoom');
    aecToggle.children('input').removeAttr('disabled');
    aecToggle.children('input').prop('checked', enabled);

    $(".connected-peers [id^='director_'] .status").html(enabled ? 'PRIVATE' : 'CONNECTED');
    $('#private-mode-badge').toggleClass('hidden', !enabled);
  }

  toggleParticipantInfo(show) {
    this.showParticipantInfo = show;
	  // send command to all participants
	  commandsManager.sendAllCommand('mirror--show-participant-info', { show: this.showParticipantInfo });
  }

  sdpTransformer(sdp, type, participantKey) {
    console.log('transforming SDP');
    let transformedSDP = sdp;
    // some options:  "VP8/90000" 96 : works on Firefox, Safari and Chrome
    //                "VP9/90000" 98 : Works on Chrome and Safari
    // only needed for SFU
    

    // we dont have access to "this", as this is a callback
    const browser = detectBrowser(window);
    if (browser.browser !== 'safari') { // safari for some reason its breaking when we modify the payload type, but otherwise works fine?
      transformedSDP = modifyCodecPayloadType(transformedSDP, 'VP9/90000', '98');
    }

    console.log("Default bandwith options")

    transformedSDP = defaultBandwithOptions(
      transformedSDP,
      qualitySettings.video.quality,
      qualitySettings.audio.bitrate,
      qualitySettings.audio.hasDTX,
      qualitySettings.audio.hasStereo,
    );
    console.log("Finish transofrming the SDP properly")
    return transformedSDP;
  }
}

function getMeParticipantComponent() {
  if (vm.$refs.orchestrator) return vm.$refs.orchestrator.$refs.meParticipant;
  if (vm.$refs.meParticipant) return vm.$refs.meParticipant;
  return {};
}

function getParticipantList() {
  if (vm.$refs.orchestrator) return vm.$refs.orchestrator.$refs.participantList;
  if (vm.$refs.participantList) return vm.$refs.participantList;
  return {};
}

function getMirrorMobileDrawerParticipantList() {
  if (vm.$refs.mirrorMobileDrawer) {
    if (vm.$refs.mirrorMobileDrawer.$refs.participantList) {
      return vm.$refs.mirrorMobileDrawer.$refs.participantList;
    }
  }
  return {};
}

export default MirrorManager;
